import { Box, Center, Flex, Link } from "@chakra-ui/react";
import { useFeeBasisRetrieveSuspense } from "gen/endpoints/fee-basis/fee-basis";
import { FeeBasisData } from "gen/model/feeBasisData";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { Control, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { FormSelect } from "shared/components/input/select/Select";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { TableRemoveButton } from "shared/components/table/TableRowActionButtons";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

interface FeePartFormSectionProps {
  feeCalculationIndex: number;
  control: Control<FeeCreateAPIDataRequest>;
  feeCalculationRemove: () => void;
  isOverride?: boolean;
}

const FeeBasisFormSelect: React.FC<{
  name: string;
  options: FeeBasisData[];
  isOverride?: boolean;
}> = ({ name, options, isOverride }) => {
  const { t } = useTranslation(["fees"]);
  const feeBasisTypeElements = Object.values(options).map((feeBasis) => ({
    value: feeBasis.id!,
    rich_value: feeBasis.name,
  }));

  return (
    <FormSelect
      name={name}
      title={t("fees:basis")}
      elements={feeBasisTypeElements}
      isDisabled={isOverride}
    />
  );
};

const FeeFormSectionParts: React.FC<FeePartFormSectionProps> = ({
  control,
  feeCalculationIndex,
  feeCalculationRemove,
  isOverride,
}) => {
  const { t } = useTranslation(["fees"]);
  const { fundFamilyId } = useRequiredStringParams();

  const { data: feeBasisData } = useFeeBasisRetrieveSuspense(fundFamilyId);
  const { fields, append, remove } = useFieldArray({
    control,
    name: `fee_calculations.${feeCalculationIndex}.fee_parts`,
  });
  return (
    <>
      <Box>
        {fields.map((field, index) => (
          <Flex
            key={field.id}
            w="full"
            flex={1}
            gap={4}
            _last={{
              "& .chakra-form-control": {
                marginBottom: 0,
              },
            }}
          >
            <Box width="60%">
              <FeeBasisFormSelect
                name={`fee_calculations[${feeCalculationIndex}].fee_parts[${index}].fee_basis_id`}
                options={feeBasisData}
                isOverride={isOverride}
              />
            </Box>
            <Box width="40%">
              <FormNumberInput
                isDisabled={false}
                name={`fee_calculations[${feeCalculationIndex}].fee_parts[${index}].fee_rate`}
                title={t("fees:feeRate")}
                valueType="percent"
              />
            </Box>
            <Center height="48px">
              <TableRemoveButton
                isDisabled={isOverride}
                onClick={() => {
                  if (fields.length === 1) {
                    feeCalculationRemove();
                  } else {
                    remove(index);
                  }
                }}
              />
            </Center>
          </Flex>
        ))}
      </Box>
      {!isOverride && (
        <Restricted permission={PermissionTypeEnum.config}>
          <Box mt={2}>
            <Link
              size="sm"
              onClick={() =>
                append({
                  fee_basis_id: "",
                  fee_rate: "",
                })
              }
            >
              {t("fees:feePart.addAnother")}
            </Link>
          </Box>
        </Restricted>
      )}
    </>
  );
};

export default FeeFormSectionParts;
